import { useMutation } from '@tanstack/react-query'
import { approveCandidate, ApproveCandidateParams } from 'supabase/desk/approveCandidate'
import { attachCandidate } from 'supabase/desk/attachCandidate'
import { decideUpdate, DecideUpdateParams } from 'supabase/desk/decideUpdate'
import { dismissMatch } from 'supabase/desk/dismissMatch'
import { rejectCandidate, RejectCandidateParams } from 'supabase/desk/rejectCandidate'
import { restoreCandidate } from 'supabase/desk/restoreCandidate'
import { restoreRawItem, RestoreRawItemParams } from 'supabase/desk/restoreRawItem'
import { saveRejectNote, SaveRejectNoteParams } from 'supabase/desk/saveRejectNote'
import { swapImage, SwapImageParams } from 'supabase/desk/swapImage'
import { undoCandidate, UndoCandidateParams } from 'supabase/desk/undoCandidate'
import { queryClient } from 'utils'
import { isDeskTranslationQuery } from './useDeskTranslation'

// Every desk decision refreshes the desk (the 'desk' prefix covers the queue,
// the counts badges, the candidate, its raw item and its siblings) and the
// Applications screens, which show the same rows (UI-SPEC §B). It runs on
// settle, not only on success: an "already decided" failure means the screen
// is stale and must re-read. The translation cache (['desk', 'translation',
// ...]) is skipped, so a decision never re-spends DeepL quota (spec-desk-auto-
// translate D-05).
const invalidate = () => {
  queryClient.invalidateQueries({ queryKey: ['desk'], predicate: (query) => !isDeskTranslationQuery(query) })
  queryClient.invalidateQueries({ queryKey: ['application'] })
  queryClient.invalidateQueries({ queryKey: ['applications'] })
}

/**
 * The desk's writes, one mutation per `desk_*` RPC. Errors are not swallowed:
 * a false/null RPC result arrives as DeskAlreadyDecidedError and every other
 * failure as the raw PostgREST error. reject, attach, dismissMatch and
 * decideUpdate resolve with the stamp they wrote, which `undo` takes back.
 * Callers use `mutateAsync` and map the error with `mapDeskError`.
 */
export const useDeskDecision = () => {
  const reject = useMutation({
    mutationFn: (params: RejectCandidateParams) => rejectCandidate(params),
    onSettled: invalidate
  })

  const saveNote = useMutation({
    mutationFn: (params: SaveRejectNoteParams) => saveRejectNote(params),
    onSettled: invalidate
  })

  const restore = useMutation({
    mutationFn: (id: number) => restoreCandidate(id),
    onSettled: invalidate
  })

  // The toasts' Undo (review AD-WR-11): desk_undo with the stamp the decision
  // returned, never desk_restore.
  const undo = useMutation({
    mutationFn: (params: UndoCandidateParams) => undoCandidate(params),
    onSettled: invalidate
  })

  const restoreRawItemMutation = useMutation({
    mutationFn: (params: RestoreRawItemParams) => restoreRawItem(params),
    onSettled: invalidate
  })

  const attach = useMutation({
    mutationFn: (id: number) => attachCandidate(id),
    onSettled: invalidate
  })

  const dismissMatchMutation = useMutation({
    mutationFn: (id: number) => dismissMatch(id),
    onSettled: invalidate
  })

  const swapImageMutation = useMutation({
    mutationFn: (params: SwapImageParams) => swapImage(params),
    onSettled: invalidate
  })

  const approve = useMutation({
    mutationFn: (params: ApproveCandidateParams) => approveCandidate(params),
    onSettled: invalidate
  })

  const decideUpdateMutation = useMutation({
    mutationFn: (params: DecideUpdateParams) => decideUpdate(params),
    onSettled: invalidate
  })

  return {
    reject,
    saveNote,
    restore,
    undo,
    restoreRawItem: restoreRawItemMutation,
    attach,
    dismissMatch: dismissMatchMutation,
    swapImage: swapImageMutation,
    approve,
    decideUpdate: decideUpdateMutation
  }
}
